import { isValidRecord } from './generics';
import { Workflow, WorkflowId, WorkflowUserSettings, isWorkflow } from '@ply-io/ply-front-shared-utilities';
import { computed } from 'vue';
import { GetWorkflowsFilter, useWorkflowsActions, useWorkflowsGetters } from '@/store-composables';
import { useCache } from '@/utils/cache';
import WorkflowService from '@/utils/WorkflowService';

const baseFilters: Pick<GetWorkflowsFilter, 'page_size'> = {
	page_size: 100,
};

const { getKey, setKeyValue } = useCache();

const CACHED_WORKFLOWS_KEY = 'workflows';
type WorkflowsQueryParamsCacheKey = string;
type WorkflowPages = Workflow[][];
const getKeyForFilters: (filters: Partial<GetWorkflowsFilter>) => WorkflowsQueryParamsCacheKey = (filters) =>
	Object.entries(filters).reduce((acc, [key, value]) => `${acc}-${key}:${JSON.stringify(value)}`, '');

const isListOfWorkflows = (listOfWorkflows: unknown[]): listOfWorkflows is Workflow[] => listOfWorkflows.every(isWorkflow);

const isListPages = (listPages: unknown): listPages is unknown[][] => {
	if (!Array.isArray(listPages)) {
		return false;
	}
	if (!listPages.every((page) => Array.isArray(page))) {
		return false;
	}
	return true;
};
const getCachedWorkflowsRecord: () => Record<WorkflowsQueryParamsCacheKey, unknown> = () => {
	const cachedWorkflows = getKey(CACHED_WORKFLOWS_KEY, {});
	if (!isValidRecord(cachedWorkflows)) {
		throw new Error('Invalid cached workflows');
	}
	return cachedWorkflows;
};

const removeWorkflowFromCache: (workflowId: WorkflowId) => void = (workflowId) => {
	const allCachedWorkflows = getCachedWorkflowsRecord();
	Object.entries(allCachedWorkflows).forEach(([, value]) => {
		if (isListPages(value)) {
			const pageWithWorkflow = value.find((page) => page.some((workflow) => isWorkflow(workflow) && workflow.id === workflowId));
			if (pageWithWorkflow) {
				const indexOfWorkflow = pageWithWorkflow.findIndex((workflow) => isWorkflow(workflow) && workflow.id === workflowId);
				pageWithWorkflow.splice(indexOfWorkflow, 1);
				setKeyValue(CACHED_WORKFLOWS_KEY, allCachedWorkflows);
			}
		}
	});
};

const getCachedWorkflowPagesForCacheKey: (cacheKey: WorkflowsQueryParamsCacheKey) => WorkflowPages = (cacheKey) => {
	const allCachedWorkflows = getCachedWorkflowsRecord();
	const cachedWorkflows = allCachedWorkflows[cacheKey] ?? [];
	if (!isListPages(cachedWorkflows)) {
		console.error('Invalid cached workflows', { cachedWorkflows, cacheKey });
		return [];
		// throw new Error('Invalid cached workflows');
	}
	return cachedWorkflows.map<Workflow[]>((page, index) => {
		if (!isListOfWorkflows(page)) {
			console.error('Invalid cached workflows page', { cachedWorkflows, cacheKey, page, index });
			return [];
			// throw new Error('Invalid cached workflows');
		}
		return page;
	});
};

const setWorkflowPagesForCacheKey: (cacheKey: WorkflowsQueryParamsCacheKey, workflows: WorkflowPages) => void = (cacheKey, workflows) => {
	const allCachedWorkflows = getCachedWorkflowsRecord();
	allCachedWorkflows[cacheKey] = workflows;
	setKeyValue(CACHED_WORKFLOWS_KEY, allCachedWorkflows);
};

const getFilterBy = (filters?: Partial<GetWorkflowsFilter>) => ({ ...baseFilters, ...filters });

const fetchWorkflows: (payload: {
	page: number;
	options?: {
		autoPaginate: boolean;
	};
	filters?: Partial<GetWorkflowsFilter>;
	cache?: boolean;
}) => Promise<Workflow[]> = async ({ page = 1, options = { autoPaginate: false }, filters, cache = true }) => {
	const { autoPaginate } = options;
	const { getWorkflows: getWorkflowsAction } = useWorkflowsActions();
	const filterBy = getFilterBy(filters);
	const response = await getWorkflowsAction({
		page,
		filterBy,
		// autoPaginate: true,
	});
	const { results, next: hasNext } = response;
	if (cache) {
		const cacheKey: WorkflowsQueryParamsCacheKey = getKeyForFilters(filterBy);
		const cachedWorkflows = getCachedWorkflowPagesForCacheKey(cacheKey);

		const cachedIndexOfPage = page - 1;
		cachedWorkflows[cachedIndexOfPage] = results;

		if (autoPaginate && !hasNext) {
			// clear rest of pages
			cachedWorkflows.splice(cachedIndexOfPage + 1);
		}
		setWorkflowPagesForCacheKey(cacheKey, cachedWorkflows);
	}
	if (autoPaginate && hasNext) {
		return [...results, ...(await fetchWorkflows({ page: page + 1, options, filters, cache }))];
	}

	return results;
};

const getWorkflows = (
	{ cached = true, filters = {} }: Partial<{ cached: boolean; filters?: Partial<GetWorkflowsFilter> }> = { cached: true, filters: {} }
) =>
	computed<Workflow[]>(() => {
		const workflows: Record<WorkflowId, Workflow> = {};
		if (cached) {
			const cacheKey = getKeyForFilters(getFilterBy(filters));
			const workflowsCachedData = getCachedWorkflowPagesForCacheKey(cacheKey);
			/** */

			Object.assign(
				workflows,
				workflowsCachedData.flat().reduce((acc, workflow) => ({ ...acc, [workflow.id]: workflow }), {})
			);
		}
		const { allWorkflows } = useWorkflowsGetters();
		Object.assign(
			workflows,
			allWorkflows.value.reduce<Record<WorkflowId, Workflow>>((acc, workflow: Workflow) => ({ ...acc, [workflow.id]: workflow }), {})
		);
		return Object.values(workflows);
	});

const changeWorkflowUserSettings = async (workflowId: WorkflowId, settings: Omit<Partial<WorkflowUserSettings>, 'workflow_id'>) =>
	useWorkflowsActions().changeWorkflowUserSettings(workflowId, settings);

const createWorkflow = async (workflow: Record<string, unknown>) => await useWorkflowsActions().createWorkflow(workflow);

const deleteWorkflow = async (workflowId: WorkflowId) => {
	await useWorkflowsActions().deleteWorkflow(workflowId);
	removeWorkflowFromCache(workflowId);
};

export const useWorkflows = () => ({ fetchWorkflows, getWorkflows, changeWorkflowUserSettings, createWorkflow, deleteWorkflow });

export const useWorkflowPermissions = (workflowId: WorkflowId) => {
	const { hasWorkflowDeletePermissions, hasWorkflowEditGeneralConfigurationPermissions, hasWorkflowEditPermissions, getWorkflow } =
		useWorkflowsGetters();
	return {
		canEditWorkflow: hasWorkflowEditPermissions.value(workflowId),
		canHideWorkflow: WorkflowService.canHideWorkflow(getWorkflow.value(workflowId)),
		canDeleteWorkflow: hasWorkflowDeletePermissions.value(workflowId),
		canEditGeneralConfiguration: hasWorkflowEditGeneralConfigurationPermissions.value(workflowId),
	};
};
